import React from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUserCheck,
  FaPercentage,
} from "react-icons/fa";
import { formatDate } from "./utils/formatDate";

export default function UserInfoCard({ user, title, type, compact = false }) {
  if (!user) return null;

  const getStatusBadge = (status, label) => {
    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
          status ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-600"
        }`}>
        {status ? (
          <FaCheckCircle className="text-xs" />
        ) : (
          <FaTimesCircle className="text-xs" />
        )}
        {label}
      </span>
    );
  };

  if (compact) {
    return (
      <div className="bg-white rounded-lg sm:rounded-xl border-2 border-red-200 shadow-md p-3 sm:p-4">
        <div className="flex items-center gap-2 sm:gap-3">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt={user.firstName}
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-red-300 flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white flex items-center justify-center text-[#FF2D38] text-base sm:text-xl font-bold border-2 border-red-500 flex-shrink-0">
              {user.firstName?.[0]?.toUpperCase() || "?"}
              {user.lastName?.[0]?.toUpperCase() || ""}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="text-base sm:text-lg font-bold text-gray-800 truncate">
              {user.firstName || ""} {user.lastName || ""}
            </h4>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {user.email}
            </p>
            {user.age && (
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {user.age} years old • {user.occupation || "N/A"}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 sm:p-4">
        <h3 className="text-base sm:text-lg md:text-xl font-bold flex items-center gap-2">
          {type === "sender" ? (
            <FaUser className="text-white text-sm sm:text-base" />
          ) : (
            <FaEnvelope className="text-white text-sm sm:text-base" />
          )}{" "}
          <span className="text-sm sm:text-base md:text-lg">{title}</span>
        </h3>
      </div>

      <div className="p-4 sm:p-5 md:p-6">
        {/* User Avatar & Basic Info */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6 pb-4 sm:pb-5 md:pb-6 border-b border-gray-200">
          <div className="flex-shrink-0">
            {user.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.firstName}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 sm:border-4 border-red-300 shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white flex items-center justify-center text-red-500 text-2xl sm:text-3xl font-bold shadow-lg border-2 sm:border-4 border-red-500">
                {user.firstName?.[0]?.toUpperCase() || "?"}
                {user.lastName?.[0]?.toUpperCase() || ""}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 break-words">
              {user.firstName || ""} {user.lastName || ""}
            </h4>
            <p className="text-xs sm:text-sm text-gray-500 break-all">
              {user.email}
            </p>
            {user.tagline && (
              <p className="text-xs sm:text-sm text-red-600 font-medium mt-1 italic break-words">
                "{user.tagline}"
              </p>
            )}
            {user.percentage && (
              <div className="mt-2">
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <FaPercentage className="text-red-500 text-xs sm:text-sm" />
                  <span className="text-xs sm:text-sm font-semibold text-gray-700">
                    Profile Completion: {user.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className="h-2 rounded-full bg-red-500"
                    style={{ width: `${user.percentage}%` }}></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2 mb-4 sm:mb-5 md:mb-6 justify-center sm:justify-start">
          {getStatusBadge(user.isActive === 1, "Active")}
          {getStatusBadge(user.isProfileComplete === 1, "Profile Complete")}
          {getStatusBadge(user.isVerified === 1, "Verified")}
          {getStatusBadge(user.isBlur === 0, "Profile Visible")}
          {getStatusBadge(user.isDisable === 0, "Not Disabled")}
          {user.isAdmin === 1 && (
            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
              <FaUserCheck className="text-xs" /> Admin
            </span>
          )}
        </div>

        {/* User Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
          {/* Personal Information */}
          <div className="space-y-2 sm:space-y-3">
            <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
              Personal Information
            </h5>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <FaIdCard className="text-[#FF2D38] w-4" />
                <span className="text-gray-600 font-medium">User ID:</span>
                <span className="text-gray-800 font-semibold">
                  #{user.id}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaEnvelope className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium">Email:</span>
                </div>
                <span className="text-gray-800 break-all">{user.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaPhone className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium">Phone:</span>
                </div>
                <span className="text-gray-800">
                  {user.contactNo || "N/A"}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaUser className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium">Gender:</span>
                </div>
                <span className="text-gray-800">{user.gender || "N/A"}</span>
              </div>
              {user.age && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Age:</span>
                  <span className="text-gray-800">{user.age} years</span>
                </div>
              )}
              {user.height && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Height:</span>
                  <span className="text-gray-800">{user.height} cm</span>
                </div>
              )}
              {user.maritalStatus && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Marital Status:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.maritalStatus}
                  </span>
                </div>
              )}
              {user.religion && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Religion:</span>
                  <span className="text-gray-800 break-words">
                    {user.religion}
                  </span>
                </div>
              )}
              {user.sect && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Sect:</span>
                  <span className="text-gray-800 break-words">
                    {user.sect}
                  </span>
                </div>
              )}
              {user.diet && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Diet:</span>
                  <span className="text-gray-800 break-words">
                    {user.diet}
                  </span>
                </div>
              )}
              {user.smoking && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Smoking:</span>
                  <span className="text-gray-800 break-words">
                    {user.smoking}
                  </span>
                </div>
              )}
              {user.alcohol && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Alcohol:</span>
                  <span className="text-gray-800 break-words">
                    {user.alcohol}
                  </span>
                </div>
              )}
              {user.languages && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Languages:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.languages}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Professional & Location Information */}
          <div className="space-y-2 sm:space-y-3">
            <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
              Professional & Location
            </h5>
            <div className="space-y-1.5 sm:space-y-2">
              {user.occupation && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Occupation:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.occupation}
                  </span>
                </div>
              )}
              {user.education && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Education:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.education}
                  </span>
                </div>
              )}
              {user.careerStatus && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Career Status:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.careerStatus}
                  </span>
                </div>
              )}
              {user.monthlyIncome && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Monthly Income:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.monthlyIncome}
                  </span>
                </div>
              )}
              {user.address && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">
                      Address:
                    </span>
                  </div>
                  <span className="text-gray-800 break-words">
                    {user.address}
                  </span>
                </div>
              )}
              {user.grewUp && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Grew Up:</span>
                  <span className="text-gray-800 break-words">
                    {user.grewUp}
                  </span>
                </div>
              )}
              {user.residential && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Residential:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.residential}
                  </span>
                </div>
              )}
              {user.marriage && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Marriage Plan:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.marriage}
                  </span>
                </div>
              )}
              {user.parenting && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">
                    Parenting Goal:
                  </span>
                  <span className="text-gray-800 break-words">
                    {user.parenting}
                  </span>
                </div>
              )}
              {user.childerns !== undefined && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-gray-600 font-medium">Children:</span>
                  <span className="text-gray-800">
                    {user.childerns === 0
                      ? "No children"
                      : `${user.childerns} children`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About Me & Hobbies */}
        {(user.aboutMe || user.hobbiesNames) && (
          <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
            {user.aboutMe && (
              <div className="mb-3 sm:mb-4">
                <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                  About Me
                </h5>
                <p className="text-gray-700 text-xs sm:text-sm leading-relaxed bg-gray-50 p-2 sm:p-3 rounded-lg border border-red-100 break-words">
                  {user.aboutMe}
                </p>
              </div>
            )}
            {user.hobbiesNames && (
              <div>
                <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                  Hobbies & Interests
                </h5>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {user.hobbiesNames.split(",").map((hobby, idx) => (
                    <span
                      key={idx}
                      className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200">
                      {hobby.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Account Status */}
        <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
          <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3">
            Account Details
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
            {user.createdDate && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium text-xs sm:text-sm">
                    Created:
                  </span>
                </div>
                <span className="text-gray-800 text-xs sm:text-sm">
                  {formatDate(user.createdDate)}
                </span>
              </div>
            )}
            {user.modifiedDate && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium text-xs sm:text-sm">
                    Modified:
                  </span>
                </div>
                <span className="text-gray-800 text-xs sm:text-sm">
                  {formatDate(user.modifiedDate)}
                </span>
              </div>
            )}
            {user.latitude && user.longitude && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                  <span className="text-gray-600 font-medium text-xs sm:text-sm">
                    Location:
                  </span>
                </div>
                <span className="text-gray-800 text-xs break-all">
                  {user.latitude}, {user.longitude}
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span className="text-gray-600 font-medium text-xs sm:text-sm">
                Login Type:
              </span>
              <div className="flex flex-wrap gap-1">
                {user.isGoogle === 1 && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                    Google
                  </span>
                )}
                {user.isApple === 1 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                    Apple
                  </span>
                )}
                {user.isGoogle === 0 && user.isApple === 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                    Email
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        {(user.daily_proposals_used !== undefined ||
          user.last_proposal_reset ||
          user.isFace ||
          user.isHidden !== undefined) && (
          <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
            <h5 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 sm:mb-3">
              Additional Information
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              {user.daily_proposals_used !== undefined && (
                <div>
                  <span className="text-gray-600">Daily Proposals Used:</span>
                  <span className="ml-2 font-semibold text-gray-800">
                    {user.daily_proposals_used}
                  </span>
                </div>
              )}
              {user.last_proposal_reset && (
                <div>
                  <span className="text-gray-600">Last Reset:</span>
                  <span className="ml-2 text-gray-800">
                    {formatDate(user.last_proposal_reset)}
                  </span>
                </div>
              )}
              {user.isFace !== undefined && (
                <div>
                  <span className="text-gray-600">Face Verification:</span>
                  {getStatusBadge(user.isFace === 1, "")}
                </div>
              )}
              {user.isHidden !== undefined && (
                <div>
                  <span className="text-gray-600">Hidden:</span>
                  {getStatusBadge(user.isHidden === 0, "Visible")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
